import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Mail, MapPin, Phone, Clock, ArrowRight } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { Reveal } from "../components/Reveal";
import { CalendlyButton } from "../components/CalendlyButton";
import { useConfig } from "../context/ConfigContext";
import { SITE } from "../config/site";
import {
  PRACTICE_AREA_OPTIONS, URGENCY_OPTIONS, CONTACT_METHOD_OPTIONS,
} from "../data/content";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const CONSENT_LABEL =
  "I understand that submitting this form does not create an attorney-client relationship and that I should not submit highly sensitive or confidential information through this form.";

const initialForm = {
  fullName: "", phone: "", email: "", practiceArea: "", description: "",
  urgency: "", preferredContact: "", consent: false, website: "",
};

const inputClass =
  "h-12 rounded-sm border-navy/20 bg-white text-navy focus-visible:ring-gold focus-visible:ring-offset-0";

export default function Contact() {
  useSeo({
    title: "Contact & Consultation | Nolen Law Chicago",
    description:
      "Request a consultation with The Law Office of Ronald K. Nolen, LLC. Share a brief overview of your legal matter and the Chicago firm will review your inquiry.",
    path: "/contact",
  });

  const { api, calendlyUrl } = useConfig();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Please enter your full name.";
    if (!form.phone.trim()) e.phone = "Please enter a phone number.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.practiceArea) e.practiceArea = "Please select a practice area.";
    if (!form.description.trim()) e.description = "Please describe your matter briefly.";
    if (!form.urgency) e.urgency = "Please select an urgency level.";
    if (!form.preferredContact) e.preferredContact = "Please select a preferred contact method.";
    if (!form.consent) e.consent = "Consent is required to submit this form.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${api}/contact`, form);
      setSubmitted(true);
      toast.success("Thank you. Your inquiry has been submitted for review.");
      setForm(initialForm);
    } catch (err) {
      const msg = err?.response?.status === 503
        ? "The inquiry service is not configured yet. Please try again later or contact the office directly."
        : "We were unable to submit your inquiry. Please try again or contact the office directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="relative overflow-hidden bg-warmwhite pt-36 pb-20 md:pt-44 md:pb-28" data-testid="contact-page">
        <div className="arch-grid pointer-events-none absolute inset-0 opacity-40" />
        <div className="river-lines pointer-events-none absolute inset-0" />

        <div className="relative mx-auto max-w-7xl px-5 md:px-8">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-600 uppercase tracking-label text-gold">Consultations</p>
            <h1 className="mt-4 font-serif text-5xl font-500 leading-[1.02] text-navy sm:text-6xl">
              Speak With Counsel
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-bluegray-text">
              Share a brief overview of your matter and the firm will review your inquiry.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-10 lg:grid-cols-[1.6fr_1fr] lg:gap-16">
            {/* FORM */}
            <Reveal>
              <div className="rounded-sm border border-navy/10 bg-white p-6 shadow-[0_10px_40px_rgba(10,25,47,0.06)] md:p-10">
                {submitted ? (
                  <div className="flex min-h-[420px] flex-col items-center justify-center text-center" data-testid="contact-success">
                    <CheckCircle2 className="h-14 w-14 text-gold" strokeWidth={1.4} />
                    <h2 className="mt-6 font-serif text-3xl font-500 text-navy">Thank you.</h2>
                    <p className="mt-3 max-w-sm text-bluegray-text">
                      Your inquiry has been submitted for review.
                    </p>
                    <button type="button" onClick={() => setSubmitted(false)} data-testid="contact-new-inquiry"
                      className="mt-8 inline-flex items-center gap-2 rounded-sm border border-navy/20 px-6 py-3 text-sm font-600 text-navy transition-colors hover:border-gold">
                      Submit Another Inquiry
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate className="space-y-6" data-testid="contact-form">
                    {/* Honeypot */}
                    <input type="text" tabIndex={-1} autoComplete="off" value={form.website}
                      onChange={(e) => set("website", e.target.value)}
                      className="hidden" aria-hidden="true" />

                    <div className="grid gap-6 sm:grid-cols-2">
                      <Field label="Full Name" htmlFor="fullName" error={errors.fullName} required>
                        <Input id="fullName" data-testid="contact-full-name" className={inputClass}
                          value={form.fullName} onChange={(e) => set("fullName", e.target.value)}
                          aria-invalid={!!errors.fullName} placeholder="Your name" />
                      </Field>
                      <Field label="Phone Number" htmlFor="phone" error={errors.phone} required>
                        <Input id="phone" type="tel" data-testid="contact-phone" className={inputClass}
                          value={form.phone} onChange={(e) => set("phone", e.target.value)}
                          aria-invalid={!!errors.phone} placeholder="(000) 000-0000" />
                      </Field>
                    </div>

                    <Field label="Email Address" htmlFor="email" error={errors.email} required>
                      <Input id="email" type="email" data-testid="contact-email" className={inputClass}
                        value={form.email} onChange={(e) => set("email", e.target.value)}
                        aria-invalid={!!errors.email} placeholder="you@example.com" />
                    </Field>

                    <Field label="Practice Area" htmlFor="practiceArea" error={errors.practiceArea} required>
                      <Select value={form.practiceArea} onValueChange={(v) => set("practiceArea", v)}>
                        <SelectTrigger id="practiceArea" data-testid="contact-practice-area"
                          className={inputClass} aria-invalid={!!errors.practiceArea}>
                          <SelectValue placeholder="Select a practice area" />
                        </SelectTrigger>
                        <SelectContent>
                          {PRACTICE_AREA_OPTIONS.map((o) => (
                            <SelectItem key={o} value={o} data-testid={`practice-opt-${o}`}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label="Brief Description of the Matter" htmlFor="description" error={errors.description} required>
                      <Textarea id="description" rows={5} data-testid="contact-description"
                        className="rounded-sm border-navy/20 bg-white text-navy focus-visible:ring-gold focus-visible:ring-offset-0"
                        value={form.description} onChange={(e) => set("description", e.target.value)}
                        aria-invalid={!!errors.description}
                        placeholder="A short summary of your situation (please avoid confidential details)." />
                    </Field>

                    <div className="grid gap-6 sm:grid-cols-2">
                      <Field label="Urgency Level" htmlFor="urgency" error={errors.urgency} required>
                        <Select value={form.urgency} onValueChange={(v) => set("urgency", v)}>
                          <SelectTrigger id="urgency" data-testid="contact-urgency"
                            className={inputClass} aria-invalid={!!errors.urgency}>
                            <SelectValue placeholder="Select urgency" />
                          </SelectTrigger>
                          <SelectContent>
                            {URGENCY_OPTIONS.map((o) => (
                              <SelectItem key={o} value={o} data-testid={`urgency-opt-${o}`}>{o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Preferred Contact Method" htmlFor="preferredContact" error={errors.preferredContact} required>
                        <Select value={form.preferredContact} onValueChange={(v) => set("preferredContact", v)}>
                          <SelectTrigger id="preferredContact" data-testid="contact-preferred"
                            className={inputClass} aria-invalid={!!errors.preferredContact}>
                            <SelectValue placeholder="Select method" />
                          </SelectTrigger>
                          <SelectContent>
                            {CONTACT_METHOD_OPTIONS.map((o) => (
                              <SelectItem key={o} value={o} data-testid={`contact-opt-${o}`}>{o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>

                    {/* Consent */}
                    <div>
                      <div className="flex items-start gap-3 rounded-sm border border-navy/10 bg-cream/60 p-4">
                        <Checkbox id="consent" data-testid="contact-consent" checked={form.consent}
                          onCheckedChange={(v) => set("consent", v === true)}
                          className="mt-0.5 border-navy/40 data-[state=checked]:bg-navy data-[state=checked]:border-navy" />
                        <Label htmlFor="consent" className="text-sm font-400 leading-relaxed text-navy/80">
                          {CONSENT_LABEL}
                        </Label>
                      </div>
                      {errors.consent && (
                        <p className="mt-2 text-sm text-destructive" data-testid="error-consent">{errors.consent}</p>
                      )}
                    </div>

                    <button type="submit" disabled={submitting} data-testid="contact-submit"
                      className="group inline-flex w-full items-center justify-center gap-2 rounded-sm bg-navy px-8 py-4 text-sm font-600 text-white transition-colors hover:bg-navy-light disabled:opacity-60 sm:w-auto">
                      {submitting ? "Submitting…" : "Submit Inquiry"}
                      {!submitting && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>

            {/* SIDEBAR */}
            <Reveal delay={0.12}>
              <div className="space-y-6">
                <div className="rounded-sm border border-navy/10 bg-cream p-8">
                  <h2 className="font-serif text-2xl font-500 text-navy">Office Details</h2>
                  <ul className="mt-6 space-y-5 text-sm">
                    <ContactRow icon={Phone} label="Office Phone">
                      <a href={`tel:${SITE.phoneRaw}`} data-testid="contact-phone-link"
                        className="text-bluegray-text transition-colors hover:text-navy">
                        {SITE.phone}
                      </a>
                    </ContactRow>
                    <ContactRow icon={Mail} label="Office Email">
                      <a href={`mailto:${SITE.publicEmail}`} data-testid="contact-email-link"
                        className="break-all text-bluegray-text transition-colors hover:text-navy">
                        {SITE.publicEmail}
                      </a>
                    </ContactRow>
                    <ContactRow icon={MapPin} label="Office Address">
                      <address className="not-italic leading-relaxed text-bluegray-text">
                        {SITE.addressLines.map((line) => (<div key={line}>{line}</div>))}
                      </address>
                    </ContactRow>
                    <ContactRow icon={Clock} label="Office Hours">
                      <div className="leading-relaxed text-bluegray-text">
                        {SITE.officeHoursLines.map((line) => (<div key={line}>{line}</div>))}
                      </div>
                    </ContactRow>
                  </ul>
                </div>

                <div className="rounded-sm border border-navy/10 bg-navy p-8 text-white">
                  <h2 className="font-serif text-2xl font-500">Prefer to Schedule?</h2>
                  <p className="mt-3 text-sm leading-relaxed text-white/70">
                    {calendlyUrl
                      ? "Book a time directly using online scheduling."
                      : "Online scheduling will be available soon. In the meantime, please submit the inquiry form and the firm will follow up."}
                  </p>
                  {calendlyUrl && (
                    <div className="mt-6">
                      <CalendlyButton testid="contact-calendly" />
                    </div>
                  )}
                </div>

                <p className="text-xs leading-relaxed text-navy/50">
                  {SITE.disclaimer}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, htmlFor, error, required, children }) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mb-2 block text-sm font-600 text-navy">
        {label}{required && <span className="ml-1 text-gold">*</span>}
      </Label>
      {children}
      {error && <p className="mt-1.5 text-sm text-destructive" data-testid={`error-${htmlFor}`}>{error}</p>}
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, children }) {
  return (
    <li className="flex items-start gap-3">
      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold" strokeWidth={1.5} />
      <div>
        <p className="font-600 text-navy">{label}</p>
        {children ?? <p className="text-bluegray-text">{value}</p>}
      </div>
    </li>
  );
}
